/**
 * BelgeKurallariPage.jsx — Belge Kuralları
 * Kendi rotası YOK (2026-09-15): iki ekrana gömülü kullanılıyor —
 *   · FuarKurallariPage.jsx "Belge Kuralları" sekmesi, scope="fuar" (/Management/Fairs/Rules?tab=belge;
 *     eski /Management/Projects/DocumentRules oraya yönleniyor)
 *   · GlobalKurallarPage.jsx, scope="global".
 * Roller: [1,2,3]
 *
 * ⚠️ KAPSAM ARTIK BİR PROP, EKRAN İÇİ ANAHTAR DEĞİL. Eskiden aynı sayfanın tepesinde
 *    "Bu Fuar / Global" düğme çifti vardı; fuar kuralı düzenlemek günlük iş, global
 *    kurala dokunmak ise ileride açılacak HER fuarı etkileyen nadir bir iş, ve ikisi
 *    tek tıkla yer değiştirebiliyordu. Kapsam artık route'a gömülü: bu sayfadan
 *    globale yazmanın yolu yok.
 *
 * Denetçi/organizatör ekranı: bir standın HANGİ belgeleri yüklemesi gerektiğini
 * belirleyen kurallar (belge_kural). Kaynak: docs/guides/FE-fuar-kural-sistemi-guide.md
 * Layer C (2026-08-24).
 *
 * Eskiden bu cevap yalnızca "Stand Konfigürasyonları" (ProjectSystemsPage.jsx,
 * project_stand_system) ekranından geliyordu; oradaki yükseklik BANTLARI çakışıyordu
 * (aynı tipte hem 0,00–3,50 hem 0,00–4,00) ve bir insanın hangisini kastettiğini
 * seçmesi gerekiyordu. Buradaki kural bunun yerine AÇIK bir yarı-açık aralık kullanır:
 * [hMin, hMax) — hMin dahil, hMax hariç. Böylece [0, 4) ile [4, 6) boşluksuz ve
 * çakışmasız döşenir, tam 4,00 m'lik bir stand ikincisine düşer.
 *
 * ⚠️ ESKİ SİSTEM YERİNDE DURUYOR, bu onun ÜSTÜNE oturdu (5 seviyeli kaskad, bkz.
 *    belgeKuralService.js). Tablo boş olduğu sürece her proje eskisi gibi çözülür —
 *    bu ekranı hiç kullanmamak güvenlidir.
 * ⚠️ Kural yazmak/silmek, o kurala göre ZATEN çözülmüş projelere geri dönmez.
 *    requiredFileIds bir anlık görüntüdür; mevcut projelere itmek için sayfadaki
 *    "Mevcut Projelere Uygula" (reresolve) düğmesi kullanılır.
 *
 * #58 (2026-09-17) — fuar sekmesi globalden MİRAS alır: fuarın kendi kurallarının
 * altında global kurallar salt-okunur listelenir, her satırda bu fuarda hâlâ geçerli
 * mi yoksa bir fuar kuralınca geçersiz mi kılındığı yazar; "Bu fuar için değiştir"
 * global satırı ekleme modalına kopyalar ve FUAR kuralı olarak kaydeder.
 */

import React, { useContext, useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Container, Card, CardBody, CardHeader, Row, Col,
  Button, Badge, Table,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Alert, Spinner, Input, FormGroup, Label,
} from "reactstrap";
import {
  Plus, Trash2, Pencil, RefreshCw, AlertTriangle, Save, Copy,
} from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MainContext from "../../../Components/Contexts/MainContext";
import { StandTypeOptions } from "../../../Components/Projects/StandTypeAgaclari";
import { getStandTypes, getAllStandFiles, getStandTypePath, reresolveFuar } from "../../../services/projectService";
import {
  getBelgeKurallari, createBelgeKural, updateBelgeKural,
  setBelgeKuralDosyalar, deleteBelgeKural,
  rangesOverlap, rangeCoveredBy, formatRange,
} from "../../../services/belgeKuralService";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

// WAS (until #398): only the two seeded test fairs ran on the real backend, every other fair fell back to mock data:
// const LIVE_TEST_FUAR_IDS = [298, 299];
// const isLiveTestFuarId = (fuarId) => LIVE_TEST_FUAR_IDS.includes(Number(fuarId));
// #398: a full-simulation test creates a NEW fair, so every real fair id is live.
const isLiveTestFuarId = (fuarId) => Number(fuarId) > 0;

const EMPTY_FORM = { standTypeId: null, hMin: "", hMax: "", aciklama: "", standFileIds: [] };

/** Canlı alan adı `files` (mock tarafında `standFiles`); standFileIds doluysa o. */
const ruleFileIds = (r) => (r.standFileIds?.length
  ? r.standFileIds
  : (r.files || r.standFiles || []).map((f) => f.stand_file_id));

const typeLabel = (r) => r.standTypeName ?? `#${r.standTypeId}`;

/** Global satırın bu fuardaki durumu → rozet. */
const INHERIT_STATUS = {
  inherited: { get label() { return i18n.t("projeler:belgeKurallari.devraliniyor"); }, color: "success-subtle", text: "text-success" },
  overridden: { get label() { return i18n.t("projeler:belgeKurallari.buFuardaGecersizKilindi"); }, color: "secondary-subtle", text: "text-secondary" },
  partial: { get label() { return i18n.t("projeler:belgeKurallari.kismenGecersizKilindi"); }, color: "warning-subtle", text: "text-warning" },
};

/**
 * FuarKurallariPage.jsx'tekiyle aynı kaskad; burada da ARA seviyede durulabilir.
 * `initialPath` (kökten tipe [{standTypeId}], getStandTypePath şekli) verilirse her
 * seviye o düğüm seçili açılır — "Bu fuar için değiştir" ön-doldurması için.
 * Modal kapanınca içerik unmount olur, yani ön-doldurma her açılışta yeniden okunur.
 */
function StandTypeCascade({ onChange, isLive, disabled, initialPath = null }) {
  const { t: tx } = useTranslation("projeler");
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  // Dizi referansı her render'da değişebilir; bağımlılık olarak id zinciri kullanılıyor.
  const initialKey = (initialPath || []).map((p) => Number(p.standTypeId)).join(">");

  // KURAL: setState Effect gövdesinde SENKRON çağrılmaz (react-hooks/set-state-in-effect)
  // — fetch mantığı Effect içinde tanımlanan bir async fonksiyona sarılır.
  useEffect(() => {
    let ignore = false;

    async function run() {
      setLoading(true);
      try {
        const ids = initialKey ? initialKey.split(">").map(Number) : [];
        const [roots, ...childLists] = await Promise.all([
          getStandTypes(null, { forceLive: isLive }),
          ...ids.map((id) => getStandTypes(id, { forceLive: isLive })),
        ]);
        const built = [{ parentId: null, options: roots || [], selectedId: "" }];
        for (let i = 0; i < ids.length; i++) {
          // Yol düğümü bu seviyenin seçeneklerinde yoksa (silinmiş/taşınmış tip) dur —
          // seçilemeyen bir değeri select'e basmak "Seçin..." gösterip tipi gizlerdi.
          if (!built[i].options.some((t) => Number(t.stand_type_id) === ids[i])) break;
          built[i].selectedId = String(ids[i]);
          const children = childLists[i] || [];
          if (children.length === 0) break;
          built.push({ parentId: ids[i], options: children, selectedId: "" });
        }
        if (!ignore) setLevels(built);
      } catch {
        if (!ignore) setLevels([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [isLive, initialKey]);

  async function handleSelect(levelIndex, typeId) {
    const next = levels.slice(0, levelIndex + 1);
    next[levelIndex] = { ...next[levelIndex], selectedId: typeId };
    setLevels(next);
    // "(bu seviyede kalsın)" seçimi bir ÜST seviyedeki seçimi İPTAL ETMEZ — kural o
    // dala yazılır. Seçilen tip her zaman "en derin dolu seviye"dir; boşaltılan
    // seviyenin üstündeki son dolu değere geri düşülür.
    const deepest = next.reduce((acc, lvl) => (lvl.selectedId ? lvl.selectedId : acc), "");
    onChange(deepest ? Number(deepest) : null);
    if (!typeId) return;
    setLoading(true);
    try {
      const children = await getStandTypes(Number(typeId), { forceLive: isLive });
      setLevels((prev) => {
        const grown = prev.slice(0, levelIndex + 1);
        if ((children || []).length > 0) grown.push({ parentId: Number(typeId), options: children, selectedId: "" });
        return grown;
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Row className="g-2">
      {levels.map((lvl, i) => (
        <Col md={4} key={`${lvl.parentId ?? "root"}-${i}`}>
          <Label size="sm">{i === 0 ? tx("belgeKurallari.standTuru") : tx("belgeKurallari.altTip", { i })}</Label>
          <Input
            type="select" bsSize="sm" disabled={disabled}
            value={lvl.selectedId} onChange={(e) => handleSelect(i, e.target.value)}
          >
            <option value="">{i === 0 ? tx("belgeKurallari.secin") : tx("belgeKurallari.buSeviyedeKalsin")}</option>
            <StandTypeOptions options={lvl.options} />
          </Input>
        </Col>
      ))}
      {loading && <Col md="auto" className="d-flex align-items-end pb-1"><Spinner size="sm" /></Col>}
    </Row>
  );
}

export default function BelgeKurallariPage({ scope = "fuar", embedded = false }) {
  const { t: tx } = useTranslation("projeler");
  const { selectedFuarId } = useContext(MainContext);
  // Global kapsam fuardan BAĞIMSIZ: GlobalKurallarPage gibi (LIVE = true) her zaman canlı.
  // Eskiden o da üst menüdeki fuara bakıyordu — 298/299 dışında bir fuar seçiliyken
  // global sekmesi sessizce mock listeyi gösterip mock'a yazıyordu.
  const isLive = scope === "global" ? true : isLiveTestFuarId(selectedFuarId);

  // fuarId null = GLOBAL kurallar. Fuar kuralları globalleri yener, o yüzden ikisi
  // ayrı listeler halinde tutuluyor — tek tabloda birleştirmek hangisinin kazandığını
  // gizlerdi. Hangi kapsamda olduğumuz çağıranın kararı (route'a gömülü), sayfa
  // içinde değiştirilemez.
  const scopedFuarId = scope === "fuar" ? selectedFuarId : null;

  const [rules, setRules] = useState([]);
  // Sadece scope="fuar": devralınan global kurallar (salt okunur) + yükleme hatası.
  // Global liste düşerse fuar tablosu yine de açılsın diye hata ayrı tutuluyor.
  const [globalRules, setGlobalRules] = useState([]);
  const [globalError, setGlobalError] = useState(null);
  // standTypeId → kökten tipe zincir (getStandTypePath). null = alınamadı → sadece aynı tip.
  const [typePaths, setTypePaths] = useState({});
  const [standFiles, setStandFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [formModal, setFormModal] = useState(false);
  const [editing, setEditing] = useState(null); // null = yeni
  // "Bu fuar için değiştir": kopyalanan global kural (null = sıradan yeni kural).
  const [overrideSource, setOverrideSource] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Gömülü kullanımda başlığı EZMEYİZ — sayfanın kimliği çağıranındır
  // (GlobalKurallarPage "Global Kurallar" yazmış olur).
  useEffect(() => {
    if (embedded) return;
    document.title = i18n.t("projeler:belgeKurallari.belgeKurallariFds");
  }, [embedded]);

  useEffect(() => {
    if (!successMsg) return undefined;
    const t = setTimeout(() => setSuccessMsg(null), 3500);
    return () => clearTimeout(t);
  }, [successMsg]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (scope === "fuar" && !selectedFuarId) return undefined;
    let ignore = false;

    async function run() {
      setLoading(true);
      setError(null);
      try {
        const inherits = scope === "fuar";
        let globalErr = null;
        const [list, files, globals] = await Promise.all([
          getBelgeKurallari(scopedFuarId, { forceLive: isLive }),
          getAllStandFiles({ forceLive: isLive }),
          // Fuar listesiyle AYNI canlı/mock modunda — iki liste farklı kaynaktan gelirse
          // "geçersiz kılındı" hesabı anlamsızlaşır.
          inherits
            ? getBelgeKurallari(null, { forceLive: isLive }).catch((e) => { globalErr = e.message; return []; })
            : Promise.resolve([]),
        ]);

        // Durum sütunu için tip zincirleri: global tipin ATALARI (atadaki fuar kuralı da
        // globali yener) ve fuar tiplerinin ataları (alt tipteki fuar kuralı = kısmen).
        // Tip başına bir çağrı; kural tabloları küçük, tekilleştiriliyor. Bir zincir
        // alınamazsa o tip için yalnızca AYNI tip eşleşmesine düşülür (bkz. chainOf).
        let paths = {};
        if (inherits && (globals || []).length > 0) {
          const typeIds = [...new Set([...(globals || []), ...(list || [])].map((r) => Number(r.standTypeId)))];
          const settled = await Promise.allSettled(
            typeIds.map((id) => getStandTypePath(id, { forceLive: isLive })));
          paths = Object.fromEntries(typeIds.map((id, i) => [
            id,
            settled[i].status === "fulfilled" && (settled[i].value || []).length > 0 ? settled[i].value : null,
          ]));
        }

        if (ignore) return;
        setRules(list || []);
        setStandFiles(files || []);
        setGlobalRules(globals || []);
        setGlobalError(globalErr);
        setTypePaths(paths);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [scope, scopedFuarId, selectedFuarId, isLive, reloadToken]);

  const refresh = useCallback(() => setReloadToken((t) => t + 1), []);

  /**
   * Her global kuralın bu fuardaki durumu. Çözümleyici fuar kurallarını projenin
   * tipinden YUKARI yürür ve aralığı yüksekliği içeren ilk kuralı alır; global
   * seviyeye ancak zincirin hiçbir fuar kuralı tutmazsa iner. Yani global G (tip T):
   *  · T veya T'nin bir ATASINDAKİ fuar kuralı G ile kesişiyorsa, kesişen yükseklikler
   *    için G bu fuarda hiç çalışmaz → birleşimleri G'nin aralığını tamamen örtüyorsa
   *    "geçersiz kılındı", örtmüyorsa "kısmen";
   *  · T'nin bir ALT tipindeki kesişen fuar kuralı G'yi yalnızca o alt dal için ezer → "kısmen".
   * ⚠️ Sadece bu fuarın BELGE KURALLARINA bakılıyor. Kaskadda FAIR_PSS (eski fuar
   *    konfigürasyonu) da GLOBAL_KURAL'ın önünde; bu ekran onu görmediği için
   *    "Devralınıyor" yazan bir satır o eski yolla ezilmiş olabilir.
   */
  const globalStatuses = useMemo(() => {
    // Zincir alınamadıysa yalnızca tipin kendisi (ata eşleşmesi yapılamaz).
    const chainOf = (typeId) => {
      const p = typePaths[Number(typeId)];
      return p ? p.map((n) => Number(n.standTypeId)) : [Number(typeId)];
    };
    const out = {};
    for (const g of globalRules) {
      const gType = Number(g.standTypeId);
      const gChain = chainOf(gType);
      const covering = rules.filter((f) =>
        gChain.includes(Number(f.standTypeId)) && rangesOverlap(f, g));
      const below = rules.filter((f) =>
        Number(f.standTypeId) !== gType &&
        chainOf(f.standTypeId).includes(gType) &&
        rangesOverlap(f, g));
      let key = "inherited";
      if (covering.length > 0) key = rangeCoveredBy(g, covering) ? "overridden" : "partial";
      else if (below.length > 0) key = "partial";
      // Tamamen ezilmişse alt tip kuralları ayrıca anılmaz — zaten hiçbir yerde çalışmıyor.
      const by = key === "overridden" ? covering : [...covering, ...below];
      out[g.id] = { key, by };
    }
    return out;
  }, [globalRules, rules, typePaths]);

  async function runAction(fn, okMsg) {
    setActionLoading(true);
    setError(null);
    try {
      await fn();
      refresh();
      setSuccessMsg(okMsg);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setActionLoading(false);
    }
  }

  function openNew() {
    setEditing(null);
    setOverrideSource(null);
    setError(null);
    setForm(EMPTY_FORM);
    setFormModal(true);
  }

  // Global satırı ekleme modalına kopyalar; kayıt handleSave'in "yeni" dalından
  // fuarId = seçili fuar ile gider (global kurala dokunulmaz).
  function openOverride(g) {
    setEditing(null);
    setOverrideSource(g);
    setError(null);
    setForm({
      standTypeId: Number(g.standTypeId),
      hMin: g.hMin ?? "",
      hMax: g.hMax ?? "",
      aciklama: g.aciklama ?? "",
      standFileIds: ruleFileIds(g).map(Number),
    });
    setFormModal(true);
  }

  function openEdit(r) {
    setEditing(r);
    setOverrideSource(null);
    setError(null);
    setForm({
      standTypeId: r.standTypeId,
      hMin: r.hMin ?? "",
      hMax: r.hMax ?? "",
      aciklama: r.aciklama ?? "",
      // Canlı alan adı `files` (mock tarafında `standFiles`) — ikisi de karşılanıyor.
      standFileIds: r.standFileIds ?? (r.files || r.standFiles || []).map((f) => f.stand_file_id),
    });
    setFormModal(true);
  }

  function toggleFile(fileId) {
    setForm((f) => ({
      ...f,
      standFileIds: f.standFileIds.includes(fileId)
        ? f.standFileIds.filter((id) => id !== fileId)
        : [...f.standFileIds, fileId],
    }));
  }

  // Backend zaten 409 ile koruyor; burada aynı kontrolü KAYDETMEDEN önce yapıp
  // kullanıcıya çakıştığı kuralı gösteriyoruz (aralık aritmetiği kafa karıştırıcı —
  // hMax hariç olduğu için 4,00 iki aralığa birden düşmüyor ve bu sezgiye ters).
  const candidate = {
    hMin: form.hMin === "" ? null : Number(form.hMin),
    hMax: form.hMax === "" ? null : Number(form.hMax),
  };
  const clashingRule = form.standTypeId
    ? rules.find((r) =>
      Number(r.standTypeId) === Number(form.standTypeId) &&
      (!editing || Number(r.id) !== Number(editing.id)) &&
      rangesOverlap(r, candidate))
    : null;
  const rangeInvalid =
    candidate.hMin != null && candidate.hMax != null && candidate.hMin >= candidate.hMax;

  async function handleSave() {
    if (!form.standTypeId || clashingRule || rangeInvalid) return;
    const ok = await runAction(async () => {
      if (editing) {
        // ⚠️ fuarId/standTypeId gövdede YOK SAYILIR — taşımak için sil & yeniden oluştur.
        await updateBelgeKural(editing.id, form, { forceLive: isLive });
        await setBelgeKuralDosyalar(editing.id, form.standFileIds, { forceLive: isLive });
      } else {
        await createBelgeKural({ ...form, fuarId: scopedFuarId }, { forceLive: isLive });
      }
    }, editing
      ? tx("belgeKurallari.kuralGuncellendi")
      : overrideSource
        ? tx("belgeKurallari.fuarKuraliOlusturulduMevcutProjeler")
        : tx("belgeKurallari.kuralOlusturuldu"));
    if (ok) setFormModal(false);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    const ok = await runAction(
      () => deleteBelgeKural(deleteTarget.id, { forceLive: isLive }),
      "Kural silindi."
    );
    if (ok) setDeleteTarget(null);
  }

  const handleReresolve = () =>
    runAction(
      () => reresolveFuar(selectedFuarId, { forceLive: isLive }),
      tx("belgeKurallari.buFuarinProjelerininZorunluBelge")
    );

  const fileName = (id) =>
    standFiles.find((f) => f.stand_file_id === id)?.stand_file_name ?? `#${id}`;

  // Düz fonksiyon (bileşen değil) — iki tablo da aynı hücreyi basıyor.
  const renderFiles = (r) => {
    const ids = ruleFileIds(r);
    // Eşleşen ama BOŞ liste "sıfır belge" demek ve kaskadı DURDURUR.
    if (ids.length === 0) {
      return (
        <span className="text-warning small">
          <AlertTriangle size={12} className="me-1" />{tx("belgeKurallari.belgeIstenmiyor")}
        </span>
      );
    }
    return (
      <div className="d-flex flex-wrap gap-1">
        {ids.map((id) => (
          <Badge key={id} color="light" className="text-dark border">{fileName(id)}</Badge>
        ))}
      </div>
    );
  };

  // Ön-doldurmada kaskadın açılacağı zincir; alınamadıysa null (bkz. modal notu).
  const overridePath = overrideSource ? typePaths[Number(overrideSource.standTypeId)] ?? null : null;

  if (scope === "fuar" && !selectedFuarId) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={tx("belgeKurallari.belgeKurallari")} pageTitle={tx("belgeKurallari.projeler")} />
          <Alert color="secondary">{tx("belgeKurallari.fuarKurallariniGormekIcinOnce")}</Alert>
        </Container>
      </div>
    );
  }

  // Global ekranına GÖMÜLÜ kullanıldığında kendi page-content/BreadCrumb'ını basmaz —
  // sayfa çerçevesi bir kez, en dışta kurulur.
  // ⚠️ Sarmalayıcı bir BİLEŞEN olarak tanımlanmıyor: her render'da yeni bir bileşen
  //    tipi üretmek tüm alt ağacı yeniden mount eder ve input'lar odağını kaybeder.
  const body = (
    <>
      {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert color="success">{successMsg}</Alert>}

      {/* 2026-09-15 sadeleştirme: kaskad açıklama kartı ve kapsam uyarıları kaldırıldı.
          Kaskad (STAND → FAIR_KURAL → FAIR_PSS → GLOBAL_KURAL → GLOBAL_PSS) dosya başında.
          #58: yerine tek satırlık bir özet — miras bölümü onsuz okunmuyor. */}
      {scope === "fuar" && (
        <p className="text-muted small mb-2">
          {tx("belgeKurallari.buFuarinKurallariGlobalKurallari")}
        </p>
      )}
      <Card>
        <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
          <h6 className="card-title mb-0 flex-grow-1">
            {scope === "fuar" ? tx("belgeKurallari.belgeKurallari") : tx("belgeKurallari.globalBelgeKurallari")}
            <Badge color="light" className="text-dark ms-1">{rules.length}</Badge>
          </h6>
          {scope === "fuar" && (
            <Button
              color="light" size="sm" className="border" disabled={actionLoading} onClick={handleReresolve}
              title={tx("belgeKurallari.kurallardakiDegisikligiBuFuarinMevcut")}
            >
              <RefreshCw size={13} className="me-1" />{tx("belgeKurallari.mevcutProjelereUygula")}
            </Button>
          )}
          <Button color="light" size="sm" className="border" onClick={refresh} disabled={loading} title={tx("common:yenile")}>
            <RefreshCw size={13} />
          </Button>
          <Button color="primary" size="sm" onClick={openNew}>
            <Plus size={14} className="me-1" />{tx("belgeKurallari.kuralEkle")}
          </Button>
        </CardHeader>
        <CardBody className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner color="primary" /></div>
          ) : rules.length === 0 ? (
            <div className="text-muted text-center py-4">
              {scope === "fuar"
                ? tx("belgeKurallari.buFuaraOzelKuralYok")
                : tx("belgeKurallari.kuralYokBelgelerStandKonfigurasyonundan")}
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="align-middle table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">{tx("belgeKurallari.standTipi")}</th>
                    <th style={{ width: 150 }}>{tx("belgeKurallari.yukseklik")}</th>
                    <th>{tx("belgeKurallari.zorunluBelgeler")}</th>
                    <th>{tx("belgeKurallari.aciklama")}</th>
                    <th style={{ width: 100 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r) => {
                    return (
                      <tr key={r.id}>
                        <td className="ps-3 fw-semibold">{typeLabel(r)}</td>
                        <td><Badge color="info-subtle" className="text-info">{formatRange(r)}</Badge></td>
                        <td>{renderFiles(r)}</td>
                        <td className="text-muted small">{r.aciklama || "—"}</td>
                        <td className="text-end pe-3">
                          <Button color="link" size="sm" className="text-body p-1" onClick={() => openEdit(r)} title={tx("common:duzenle")}>
                            <Pencil size={14} />
                          </Button>
                          <Button color="link" size="sm" className="text-danger p-1" onClick={() => setDeleteTarget(r)} title={tx("common:sil")}>
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
        <div className="card-footer text-muted small">
          {scope === "fuar" ? (
            <>{tx("belgeKurallari.fuarKurallariGlobalKurallarinOnundedir")}{" "}<Link to="/Management/Projects/GlobalRules">{tx("belgeKurallari.globalKurallar")}</Link></>
          ) : (
            <>{tx("belgeKurallari.globalKurallarHerFuardaGecerlidir")}</>
          )}
        </div>
      </Card>

      {/* ─── #58: globalden devralınanlar (salt okunur, sadece fuar kapsamı) ─── */}
      {scope === "fuar" && (
        <Card>
          <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
            <h6 className="card-title mb-0 flex-grow-1">
              {tx("belgeKurallari.globalKurallardanDevralinanlar")}
              <Badge color="light" className="text-dark ms-1">{globalRules.length}</Badge>
            </h6>
          </CardHeader>
          <CardBody className="p-0">
            {globalError && (
              <Alert color="warning" className="m-3 mb-0">{tx("belgeKurallari.globalKurallarYuklenemedi")}{" "}{globalError}</Alert>
            )}
            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : globalRules.length === 0 ? (
              !globalError && <div className="text-muted text-center py-4">{tx("belgeKurallari.globalKuralYok")}</div>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-3">{tx("belgeKurallari.standTipi")}</th>
                      <th style={{ width: 150 }}>{tx("belgeKurallari.yukseklik")}</th>
                      <th>{tx("belgeKurallari.zorunluBelgeler")}</th>
                      <th>{tx("belgeKurallari.aciklama")}</th>
                      <th style={{ width: 210 }}>{tx("belgeKurallari.buFuarda")}</th>
                      <th style={{ width: 170 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {globalRules.map((g) => {
                      const st = globalStatuses[g.id] ?? { key: "inherited", by: [] };
                      const meta = INHERIT_STATUS[st.key];
                      return (
                        <tr key={g.id} className={st.key === "overridden" ? "opacity-75" : undefined}>
                          <td className="ps-3 fw-semibold">{typeLabel(g)}</td>
                          <td><Badge color="info-subtle" className="text-info">{formatRange(g)}</Badge></td>
                          <td>{renderFiles(g)}</td>
                          <td className="text-muted small">{g.aciklama || "—"}</td>
                          <td>
                            <Badge color={meta.color} className={meta.text}>{meta.label}</Badge>
                            {/* Hangi fuar kuralının ezdiği — tip + aralık, yukarıdaki tablodan bulunsun. */}
                            {st.by.length > 0 && (
                              <div className="text-muted small mt-1">
                                {st.by.map((f) => `${typeLabel(f)} · ${formatRange(f)}`).join(", ")}
                              </div>
                            )}
                          </td>
                          <td className="text-end pe-3">
                            <Button
                              color="soft-primary" size="sm" disabled={actionLoading}
                              onClick={() => openOverride(g)}
                              title={tx("belgeKurallari.buKuraliBuFuaraOzel")}
                            >
                              <Copy size={13} className="me-1" />{tx("belgeKurallari.buFuarIcinDegistir")}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
          <div className="card-footer text-muted small">
            {tx("belgeKurallari.durumYalnizcaBuFuarinBelge")}
          </div>
        </Card>
      )}

      {/* ─── Ekle/düzenle modalı ─── */}
      <Modal isOpen={formModal} toggle={() => !actionLoading && setFormModal(false)} centered size="lg">
        <ModalHeader toggle={() => !actionLoading && setFormModal(false)}>
          {editing
            ? typeLabel(editing)
            : overrideSource ? tx("belgeKurallari.globalKuraliBuFuarIcin") : tx("belgeKurallari.belgeKuraliEkle")}
        </ModalHeader>
        <ModalBody>
          {/* Hata sayfanın tepesinde de basılıyor ama modal açıkken arkada kalıyor —
              409 (çakışma) burada görünsün diye aynı state tekrar gösteriliyor. */}
          {error && <Alert color="danger" className="py-2 small">{error}</Alert>}

          {overrideSource && (
            <p className="text-muted small mb-3">
              {tx("belgeKurallari.kaynakGlobal")}{" "}<span className="fw-semibold">{typeLabel(overrideSource)}</span>
              {tx("belgeKurallari.globalKuralDegismezBuFuar", { formatRange: formatRange(overrideSource) })}
            </p>
          )}

          {/* Düzenlemede tip/fuar değiştirilemez — backend gövdedekini yok sayar; taşımak = sil + yeniden oluştur. */}
          {!editing && (
            <div className="mb-3">
              <StandTypeCascade
                onChange={(id) => setForm((f) => ({ ...f, standTypeId: id }))}
                isLive={isLive} disabled={actionLoading}
                initialPath={overridePath}
              />
              {/* Zincir alınamadıysa kaskad boş açılır ama form tipi globalinkini taşıyor. */}
              {overrideSource && !overridePath &&
                Number(form.standTypeId) === Number(overrideSource.standTypeId) && (
                <div className="text-muted small mt-1">
                  {tx("belgeKurallari.seciliTip")}{" "}<span className="fw-semibold">{typeLabel(overrideSource)}</span>
                  {tx("belgeKurallari.degistirmekIcinYukaridanSecin")}
                </div>
              )}
            </div>
          )}

          <Row className="g-3 mb-3">
            <Col md={3}>
              <Label size="sm">{tx("belgeKurallari.altSinirMDahil")}</Label>
              <Input
                type="number" min="0" step="0.1" bsSize="sm" placeholder="—"
                value={form.hMin} onChange={(e) => setForm((f) => ({ ...f, hMin: e.target.value }))}
              />
            </Col>
            <Col md={3}>
              <Label size="sm">{tx("belgeKurallari.ustSinirMHaric")}</Label>
              <Input
                type="number" min="0" step="0.1" bsSize="sm" placeholder="—"
                value={form.hMax} onChange={(e) => setForm((f) => ({ ...f, hMax: e.target.value }))}
              />
            </Col>
            <Col md={6}>
              <Label size="sm">{tx("belgeKurallari.aciklama")}</Label>
              <Input
                type="text" bsSize="sm" placeholder={tx("belgeKurallari.orAhsap4MAlti")}
                value={form.aciklama} onChange={(e) => setForm((f) => ({ ...f, aciklama: e.target.value }))}
              />
            </Col>
          </Row>

          {rangeInvalid && <div className="text-danger small mb-2">{tx("belgeKurallari.altSinirUstSinirdanKucuk")}</div>}
          {clashingRule && (
            <div className="text-danger small mb-2">
              <AlertTriangle size={12} className="me-1" />
              {tx("belgeKurallari.ayniTiptekiKuraliylaCakisiyor", { formatRange: formatRange(clashingRule) })}
            </div>
          )}

          <Label size="sm">{tx("belgeKurallari.zorunluBelgeler2")}</Label>
          <Row className="g-2">
            {standFiles.map((f) => (
              <Col md={6} key={f.stand_file_id}>
                <FormGroup check className="mb-0">
                  <Input
                    type="checkbox" id={`bk-file-${f.stand_file_id}`}
                    checked={form.standFileIds.includes(f.stand_file_id)}
                    onChange={() => toggleFile(f.stand_file_id)}
                  />
                  <Label check for={`bk-file-${f.stand_file_id}`}>{f.stand_file_name}</Label>
                </FormGroup>
              </Col>
            ))}
          </Row>
          {/* Boş liste kaskadı DURDURUR, eski konfigürasyona düşmez. */}
          {form.standFileIds.length === 0 && (
            <div className="text-warning small mt-2">
              <AlertTriangle size={12} className="me-1" />{tx("belgeKurallari.belgeSecilmezseBuStandlardanHic")}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setFormModal(false)} disabled={actionLoading}>{tx("belgeKurallari.vazgec")}</Button>
          <Button
            color="primary" onClick={handleSave}
            disabled={actionLoading || !form.standTypeId || Boolean(clashingRule) || rangeInvalid}
          >
            {actionLoading ? <Spinner size="sm" className="me-1" /> : <Save size={14} className="me-1" />}{tx("common:kaydet")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ─── Silme onayı — zaten çözülmüş projelere geri dönmez ─── */}
      <Modal isOpen={Boolean(deleteTarget)} toggle={() => !actionLoading && setDeleteTarget(null)} centered>
        <ModalHeader toggle={() => !actionLoading && setDeleteTarget(null)}>{tx("belgeKurallari.belgeKuraliniSil")}</ModalHeader>
        <ModalBody>
          <p className="mb-2">
            <span className="fw-semibold">{deleteTarget?.standTypeName ?? `#${deleteTarget?.standTypeId}`}</span>
            {" · "}{deleteTarget ? formatRange(deleteTarget) : ""}{" "}{tx("belgeKurallari.silinecek")}
          </p>
          {scope === "fuar" && (
            <p className="text-muted small mb-0">{tx("belgeKurallari.mevcutProjelerIcinArdindanMevcut")}</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDeleteTarget(null)} disabled={actionLoading}>{tx("belgeKurallari.vazgec")}</Button>
          <Button color="danger" onClick={handleDelete} disabled={actionLoading}>
            {actionLoading ? <Spinner size="sm" className="me-1" /> : <Trash2 size={14} className="me-1" />}{tx("common:sil")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );

  if (embedded) return body;
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("belgeKurallari.belgeKurallari")} pageTitle={tx("belgeKurallari.projeler")} />
        {body}
      </Container>
    </div>
  );
}
